/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import LazyImage from "@eisinc/MobiusOverrides/LazyImage";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { Session } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    chatIconImage = "chatIconImage",
    chatIconImageAltText = "chatIconImageAltText",
    chatIconWidthHeight = "chatIconWidthHeight",
    chatIconPosition = "chatIconPosition",
}

interface OwnProps extends WidgetProps {
    session: any;
    billTo: any;
    fields: {
        [fields.chatIconImage]: string;
        [fields.chatIconWidthHeight]: string;
        [fields.chatIconImageAltText]: string;
        [fields.chatIconPosition]: string;
    };
}

const mapStateToProps: (state: ApplicationState) => { session: Session } = (state: ApplicationState) => {
    const session = getSession(state);
    const billTo = getBillToState(state, session?.billToId)?.value;

    return {
        session,
        billTo,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ChatIconStyles {
    wrapper: InjectableCss;
}

export const chatIconStyles: ChatIconStyles = {
    wrapper: {
        css: css`
            overflow: hidden;
            border-radius: 50%;
            width: ${(props: any) => props?.chatIconWidthHeight}px;
            height: ${(props: any) => props?.chatIconWidthHeight}px;
            cursor: pointer;
            z-index: ${getProp("theme.zIndex.chatIcon")};
            position: fixed;
            top: ${(props: any) =>
                props?.chatIconPosition === "topLeft" || props?.chatIconPosition === "topRight" ? "15px" : "auto"};
            right: ${(props: any) =>
                props?.chatIconPosition === "topRight" || props?.chatIconPosition === "bottomRight" ? "15px" : "auto"};
            bottom: ${(props: any) =>
                props?.chatIconPosition === "bottomLeft" || props?.chatIconPosition === "bottomRight"
                    ? "15px"
                    : "auto"};
            left: ${(props: any) =>
                props?.chatIconPosition === "topLeft" || props?.chatIconPosition === "bottomLeft" ? "15px" : "auto"};
        `,
    },
};

const styles = chatIconStyles;
const StyledWrapperX = getStyledWrapper("div") as any;

const ChatIcon: FC<Props> = ({ fields, session, billTo }) => {
    const handleClick = () => {
        const button = document.querySelector('[class^="acOpenButton"]') as HTMLButtonElement | null;
        if (button) {
            button.click();
        }
    };

    return (
        <StyledWrapperX
            {...styles.wrapper}
            chatIconWidthHeight={fields.chatIconWidthHeight || "85"}
            chatIconPosition={fields.chatIconPosition}
            onClick={handleClick}
        >
            <LazyImage src={fields.chatIconImage} altText={fields.chatIconImageAltText} />
        </StyledWrapperX>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ChatIcon),
    definition: {
        group: "Basic",
        displayName: "Chat Icon",
        fieldDefinitions: [
            {
                name: fields.chatIconImage,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.chatIconImageAltText,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.chatIconWidthHeight,
                displayName: "Chat Icon Width/Height",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "85",
            },
            {
                name: fields.chatIconPosition,
                displayName: "Chat Icon Position",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                isRequired: true,
                defaultValue: "bottomRight",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
